import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {KeyboardEventHandler} from 'react';
import {Disposers, toastrHelper} from '../../../lib/helpers';
import {LanguageListLoader} from './LanguageListLoader';
import {AddLanguageStore} from './parts/AddLanguageStore';
import {DeleteLanguageStore} from './parts/DeleteLanguageStore';
import {EditLanguageStore} from './parts/EditLanguageStore';
import {ReorderLanguagesStore} from './parts/ReorderLanguagesStore';

type Mode = 'View' | 'Add' | 'Edit' | 'Delete' | 'ReOrder';

export class LanguageListStore {
  @observable
  mode: Mode;

  readonly loader: LanguageListLoader;

  @observable.ref
  addLanguage: AddLanguageStore | null;
  @observable.ref
  editLanguage: EditLanguageStore | null;
  @observable.ref
  deleteLanguage: DeleteLanguageStore | null;
  @observable.ref
  reorder: ReorderLanguagesStore | null;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.mode = 'View';

    this.loader = new LanguageListLoader();
    this.disposers.add(this.loader);
    this.addLanguage = this.editLanguage = this.deleteLanguage = this.reorder = null;

    this.setup();
  }

  private setup() {
    this.disposers.add(reaction(
      () => [
        this.addLanguage?.save?.value,
        this.editLanguage?.save?.value,
        this.deleteLanguage?.save?.value,
        this.reorder?.save?.value
      ],
      this.reloadOnSuccessResponse
    ));
  }

  @computed
  get isSaving(): boolean {
    return (
      this.addLanguage?.save?.isPending === true ||
      this.editLanguage?.save?.isPending === true ||
      this.reorder?.save?.isPending === true ||
      this.deleteLanguage?.save?.isPending === true);
  }

  @action
  clear = () => {
    this.mode = 'View';
    this.addLanguage = this.editLanguage = this.deleteLanguage = this.reorder = null;
  };

  @action
  beginAdd = () => {
    this.mode = 'Add';
    this.addLanguage = new AddLanguageStore();
  };

  @action
  completeOnEnter: KeyboardEventHandler = (event) => {
    if (event.key !== 'Enter')
      return;
    if (this.mode === 'Add')
      this.completeAdd();
    if (this.mode === 'Edit')
      this.completeEdit();
  };

  @action
  completeAdd = () => {
    this.addLanguage?.complete();
  };

  @action
  beginEdit = (languageId: number) => {
    this.mode = 'Edit';
    let language = this.loader.languageById[languageId]!;
    this.editLanguage = new EditLanguageStore(language);
  };

  @action
  completeEdit = () => {
    this.editLanguage?.complete();
  };

  @action
  beginDelete = (languageId: number) => {
    this.mode = 'Delete';
    this.deleteLanguage = new DeleteLanguageStore(languageId);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deleteLanguage?.complete();
  };

  @action
  beginReorder = () => {
    this.mode = 'ReOrder';
    const languageIds = this.loader.languages.map(x => x.Id);
    this.reorder = new ReorderLanguagesStore(languageIds);
  };

  @action
  reorderUp = (index: number) => {
    this.reorder?.swap(index, index - 1);
  };

  @action
  reorderDown = (index: number) => {
    this.reorder?.swap(index, index + 1);
  };


  @action
  completeReorder = () => {
    this.reorder?.complete();
  };

  @action
  cancel = () => {
    if (this.isSaving)
      return;
    this.clear();
  };

  @action
  private reloadOnSuccessResponse = () => {
    for (let state of [
      this.addLanguage?.save,
      this.editLanguage?.save,
      this.deleteLanguage?.save,
      this.reorder?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.loader.refresh();
        this.clear();
      }
  };

  dispose() {
    this.disposers.dispose();
  }
}
